import { useAuth } from '@data-delivery/okta-auth-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export interface CurrentUserInfoData {
  claims?: Record<string, unknown>;
  tokens: {
    accessToken?: string;
    idToken?: string;
    accessTokenExpiresAt?: number;
    idTokenExpiresAt?: number;
  };
}

export function useCurrentUserInfoQuery(): UseQueryResult<CurrentUserInfoData> {
  const auth = useAuth();

  return useQuery({
    queryKey: ['userInfo'],
    async queryFn() {
      const [user, tokenSet] = await Promise.all([auth.getUser(), auth.getTokens()]);
      const accessTokenObj = tokenSet.accessToken as unknown as
        | { accessToken?: string; expiresAt?: number }
        | undefined;
      const idTokenObj = tokenSet.idToken as unknown as
        | { idToken?: string; expiresAt?: number }
        | undefined;

      return {
        claims: (user as Record<string, unknown>) || undefined,
        tokens: {
          accessToken: accessTokenObj?.accessToken,
          idToken: idTokenObj?.idToken,
          accessTokenExpiresAt: accessTokenObj?.expiresAt,
          idTokenExpiresAt: idTokenObj?.expiresAt,
        },
      } satisfies CurrentUserInfoData;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: prev => prev,
  });
}
